import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../button/button';
import { SecretInput } from '../secret-input';
import {
  removeAnthropicKey,
  saveAnthropicKey,
} from '../../../lib/managed-agents/api';
import type { WorkspaceKeyStatus } from '../../../lib/managed-agents/types';
import { ANTHROPIC_KEYS_URL } from '../connections/constants';

interface AnthropicKeySectionProps {
  workspaceId: string;
  isAdmin: boolean;
  status: WorkspaceKeyStatus;
  onChange: (status: WorkspaceKeyStatus) => void;
}

/** The workspace's Anthropic key: one per workspace, managed by admins. */
export function AnthropicKeySection({
  workspaceId,
  isAdmin,
  status,
  onChange,
}: AnthropicKeySectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const run = async (
    action: () => Promise<WorkspaceKeyStatus>,
    success: string,
  ) => {
    setIsBusy(true);
    try {
      onChange(await action());
      setApiKey('');
      setIsEditing(false);
      setIsConfirmingRemove(false);
      toast.success(success);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Try again.');
    } finally {
      setIsBusy(false);
    }
  };

  const showInput = isAdmin && (!status.connected || isEditing);
  const showActions =
    status.connected && isAdmin && !isEditing && !isConfirmingRemove;

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-[#00000014] bg-prompt-safecomwhite p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-prompt-safecomvulcan">
            Anthropic API key
          </h3>
          <p className="text-xs text-prompt-safecomabbey">
            {status.connected
              ? `Connected · ends in ${status.keyHint}`
              : 'Shared by your workspace and stored encrypted.'}
          </p>
        </div>
        {showActions && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Replace
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmingRemove(true)}
            >
              Remove
            </Button>
          </div>
        )}
      </div>

      {isConfirmingRemove && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-red-50 px-3 py-2">
          <p className="text-sm text-red-800">
            Remove the key? Imported agents stop working until a new key is
            saved.
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmingRemove(false)}
            >
              Keep key
            </Button>
            <Button
              size="sm"
              disabled={isBusy}
              className="bg-red-700 text-white hover:bg-red-800"
              onClick={() =>
                run(
                  () => removeAnthropicKey(workspaceId),
                  'Anthropic key removed',
                )
              }
            >
              Remove key
            </Button>
          </div>
        </div>
      )}

      {showInput && (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            run(
              () => saveAnthropicKey(workspaceId, apiKey),
              'Anthropic key saved',
            );
          }}
        >
          <SecretInput
            aria-label="Anthropic API key"
            placeholder="sk-ant-…"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            className="bg-prompt-safecomwhite"
          />
          <Button type="submit" size="sm" disabled={isBusy || !apiKey.trim()}>
            {isBusy ? 'Checking…' : 'Save'}
          </Button>
          {isEditing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
          )}
        </form>
      )}

      {showInput && !status.connected && (
        <a
          href={ANTHROPIC_KEYS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 self-start text-xs font-medium text-prompt-safecomoutrageous-orange hover:underline"
        >
          Get a key in Claude Console
          <ArrowUpRight className="h-3 w-3" />
        </a>
      )}

      {!status.connected && !isAdmin && (
        <p className="text-sm text-prompt-safecomabbey">
          Ask a workspace admin to add an Anthropic API key.
        </p>
      )}
    </section>
  );
}
